import { z } from 'zod';

import { CancelReason } from '../organization-billing';
import { organizationUserSchema } from '../organization-user';
import { userCreateSchema, userSchema } from '../user';

//////////////////////////
// Create
export const createUserReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z
    .object({
      // utm data will be passed in as utm_source / utm_medium
      signupSource: userCreateSchema.shape['signupSource'],
      signupMedium: userCreateSchema.shape['signupMedium'],
      signupCampaign: userCreateSchema.shape['signupCampaign'],
      signupTerm: userCreateSchema.shape['signupTerm'],
      // ad platform click IDs
      signupGclid: userCreateSchema.shape['signupGclid'],
      signupFbclid: userCreateSchema.shape['signupFbclid'],
      signupLiFatId: userCreateSchema.shape['signupLiFatId'],
    })
    .nullish(),
});
export type CreateUserReq = z.infer<typeof createUserReqSchema>;

export const createUserResSchema = {
  200: z.object({
    data: userSchema,
    created: z.boolean(),
  }),
};
export type CreateUserRes = z.infer<(typeof createUserResSchema)[200]>;

//////////////////////////
// Get auth
export const getAuthReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetAuthReq = z.infer<typeof getAuthReqSchema>;

export const getAuthResSchema = {
  200: z.object({
    data: userSchema,
  }),
};
export type GetAuthRes = z.infer<(typeof getAuthResSchema)[200]>;

//////////////////////////
// Get users by ids
export const getUsersByIdsReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({
    userIds: z.array(userSchema.shape._id),
  }),
});
export type GetUsersByIdsReq = z.infer<typeof getUsersByIdsReqSchema>;

export const getUsersByIdsResSchema = {
  200: z.object({
    data: z.array(userSchema),
  }),
};
export type GetUsersByIdsRes = z.infer<(typeof getUsersByIdsResSchema)[200]>;

//////////////////////////
// Save
export const saveUserReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: userCreateSchema.partial().strip(),
});
export type SaveUserReq = z.infer<typeof saveUserReqSchema>;

export const saveUserResSchema = {
  200: z.object({
    data: userSchema,
  }),
};
export type SaveUserRes = z.infer<(typeof saveUserResSchema)[200]>;

//////////////////////////
// Get organizations
export const getUserOrganizationsReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z.object({}).nullish(),
});
export type GetUserOrganizationsReq = z.infer<typeof getUserOrganizationsReqSchema>;

export const getUserOrganizationsResSchema = {
  200: z.object({
    data: z.array(organizationUserSchema),
  }),
};
export type GetUserOrganizationsRes = z.infer<(typeof getUserOrganizationsResSchema)[200]>;

//////////////////////////
// User get socket event
export const getUserSocketResSchema = z
  .object({
    user: userSchema,
    storageVersion: z.object({
      organization: z.number(),
      brand: z.number(),
      content: z.number(),
      user: z.number(),
    }),
  })
  .or(
    z.object({
      userId: userSchema.shape._id,
      hasError: z.boolean(),
      isDeleted: z.boolean(),
    })
  );
export type GetUserSocketRes = z.infer<typeof getUserSocketResSchema>;

//////////////////////////
// Delete
export const deleteUserReqSchema = z.object({
  params: z.object({}).nullish(),
  query: z.object({}).nullish(),
  body: z
    .object({
      reason: z.nativeEnum(CancelReason),
      extraDetails: z.string().nullish(),
    })
    .nullish(),
});
export type DeleteUserReq = z.infer<typeof deleteUserReqSchema>;

export const deleteUserResSchema = {
  200: z.object({
    ok: z.boolean(),
  }),
};
export type DeleteUserRes = z.infer<(typeof deleteUserResSchema)[200]>;
